import { useState, type FormEvent } from 'react';
import { signIn, signOut } from '../core/msal';
import { requestMagicLink } from '../core/api';
import type { Me } from '../core/types';
import { useTheme } from './useTheme';
import { ThemeSwitch } from './ThemeSwitch';
import s from './Landing.module.css';

// The front door. Managed sign-in via Entra (MSAL): email & password, self-signup,
// and the session sticks (no more ~8h logout). Signing in for the first time creates
// your account. After sign-in/out we re-boot the app (onAuthed) so identity refreshes.

const Note = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9 18V5l12-2v13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>;

export function Landing({ paused = false, me, onEnter, authError = null }: { paused?: boolean; me: Me | null; onEnter: () => void; authError?: string | null }) {
  const { mode } = useTheme();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const signedIn = !!me?.user;
  const isAdmin = !!me?.user?.is_admin;

  // signIn/signOut redirect the whole tab to Entra and back; the app re-boots on
  // return (initMsal → handleRedirectPromise finishes it), so there's nothing to do
  // after — busy just stays until the navigation happens.
  async function onSignIn(force = false) {
    setBusy(true); setErr(null);
    try {
      await signIn(force);
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  }

  async function onSignOut() {
    setBusy(true); setErr(null);
    try {
      await signOut();
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  }

  // Email magic-link — the reliable primary path (never touches Microsoft, so no
  // browser extension can block it). Enter email → get a link → click → signed in.
  const [email, setEmail] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  async function onEmail(e: FormEvent) {
    e.preventDefault();
    setEmailBusy(true); setEmailErr(null);
    try {
      await requestMagicLink(email.trim());
      setEmailSent(true);
    } catch (er) {
      setEmailErr(er instanceof Error ? er.message : 'Could not send the link.');
    } finally {
      setEmailBusy(false);
    }
  }
  const emailSignIn = emailSent ? (
    <p className={s.note} style={{ fontSize: 15 }}>
      ✅ Check your inbox — we sent a sign-in link to <b>{email}</b>. It works once and expires in 15 minutes.
    </p>
  ) : (
    <form onSubmit={onEmail} className={s.actions} style={{ flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
      <input
        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com" autoComplete="email"
        style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid var(--line, #d0d4da)', fontSize: 15, width: '100%', boxSizing: 'border-box' }}
      />
      <button type="submit" className={`${s.btn} ${s.primary}`} disabled={emailBusy}>
        {emailBusy ? 'Sending…' : 'Email me a sign-in link →'}
      </button>
      {emailErr ? <span className={s.note} style={{ color: 'var(--video)' }}>{emailErr}</span> : null}
    </form>
  );

  return (
    <div className="tp-root" data-mode={mode}>
      <div className={s.root}>
        <div className={s.toggle}><ThemeSwitch /></div>

        <div className={s.card}>
          <div className={s.brand}>
            <span className={s.tile}><Note /></span>
            <span className={s.word}>TPIAP</span>
          </div>

          {paused ? <div className={s.paused}>● League paused</div> : null}

          {signedIn ? (
            <>
              <h1 className={s.h}>Welcome back, {me!.user!.display_name}</h1>
              <p className={s.sub}>
                {isAdmin
                  ? paused ? 'The league is paused. Head in to the console to switch it back on.' : "You're signed in."
                  : paused ? 'The league is paused right now — sit tight, it’ll be back.' : "You're signed in."}
              </p>
              <div className={s.actions}>
                {isAdmin ? <button className={`${s.btn} ${s.primary}`} onClick={onEnter}>Enter the league →</button> : null}
                <button className={s.link} onClick={onSignOut} disabled={busy}>{busy ? 'Signing out…' : 'Sign out'}</button>
              </div>
            </>
          ) : authError ? (
            // A sign-in round-trip came back without a session (the old silent loop).
            // Say so plainly, offer a retry, and point at the usual culprits instead of
            // bouncing the user through the same broken attempt again.
            <>
              <h1 className={s.h}>Sign in with your email instead</h1>
              <p className={s.sub}>Microsoft sign-in didn’t complete — often a browser extension blocking it. Email always works:</p>
              {emailSignIn}
              <p className={s.note} style={{ marginTop: 14 }}>
                <button className={s.link} onClick={() => onSignIn(true)} disabled={busy} style={{ padding: 0 }}>
                  {busy ? 'Opening…' : 'or try Microsoft again'}
                </button>
                <br /><span style={{ opacity: 0.6 }}>Details: {authError}</span>
              </p>
            </>
          ) : (
            <>
              <h1 className={s.h}>This Place Is A Playlist</h1>
              <p className={s.sub}>Your crew, one playlist a round. Sign in with your email to submit, vote, and talk trash.</p>
              {emailSignIn}
              <p className={s.note} style={{ margin: '14px 0 4px' }}>New here? The link creates your account — no password to remember.</p>
              <button className={s.link} onClick={() => onSignIn()} disabled={busy} style={{ marginTop: 2 }}>
                {busy ? 'Opening…' : 'or continue with Microsoft'}
              </button>
            </>
          )}

          {err ? <p className={s.note} style={{ color: 'var(--video)' }}>Couldn’t sign in: {err}</p> : null}
        </div>

        {!signedIn && !authError ? (
          <div className={s.pitch}>
            <div className={s.pitchrow}>
              <span className={s.pstep}><b>🎵 Drop a track</b>Each round has a theme. Everyone submits one song (or video, or image) — anonymously.</span>
              <span className={s.pstep}><b>🗳️ Rank & guess</b>Spend points to rank the others, then guess who submitted what. Optionally veto one.</span>
              <span className={s.pstep}><b>🏆 Climb the boards</b>Score for great picks, for guessing right, and for staying hidden. Talk trash in chat.</span>
            </div>
            <p className={s.pnote}>A new playlist every round. Run the themes yourself, or let everyone propose them. <a className={s.morelink} href="/about">What is this? →</a></p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function errorText(e: unknown): string {
  if (e instanceof Error) {
    if (/user_cancelled|popup_window_error|window_close|user_cancel/i.test(e.message)) return 'sign-in was cancelled.';
    return e.message;
  }
  return 'something went wrong.';
}
